import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule, DOCUMENT, NgOptimizedImage } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs';
import { selectProductById } from '../../store/webshop/webshop.selectors';
import { EnrichedProduct } from '../../store/webshop/webshop.models';
import { ContainerComponent } from '../../components/container/container.component';
import { HeaderComponent } from '../../components/header/header.component';
import { Meta, Title } from '@angular/platform-browser';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-product',
  standalone: true,
  imports: [
    CommonModule,
    ContainerComponent,
    HeaderComponent,
    NgOptimizedImage,
    RouterLink,
    TooltipModule,
    ToastModule,
    DialogModule
  ],
  providers: [MessageService],
  templateUrl: './product.component.html',
  styleUrl: './product.component.scss'
})
export class ProductComponent implements OnInit {
  product = signal<EnrichedProduct | undefined>(undefined);
  selectedImageIndex = signal(0);
  zoomOrigin = signal('center');
  displayImagePopup = signal(false);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private messageService = inject(MessageService);
  private document = inject(DOCUMENT);

  title = computed(() => {
    return this.product()?.enrichedAttributes.find(attr => attr.name === 'Titel')?.displayValue;
  });

  selectedImage = computed(() => {
    const images = this.product()?.images;
    if (images && images.length > 0) {
      return images[this.selectedImageIndex()];
    }
    return undefined;
  });

  productCode = computed(() => {
    return this.product()?.enrichedAttributes.find(attr => attr.name === 'Productcode')?.displayValue;
  });

  remark = computed(() => {
    return this.product()?.enrichedAttributes.find(attr => attr.name === 'Opmerking')?.displayValue;
  });

  otherAttributes = computed(() => {
    return this.product()?.enrichedAttributes.filter(attr =>
      attr.name !== 'Productcode' &&
      attr.name !== 'Titel' &&
      attr.name !== 'Opmerking'
    ) ?? [];
  });

  backLink = computed(() => {
    const category = this.product()?.category;
    if (category) {
      if (category.parentCategory) {
        return ['/producten', category.parentCategory.slug, category.slug];
      }
      return ['/producten', category.slug];
    }
    return ['/producten'];
  });

  handleMouseMove(event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    const { left, top, width, height } = target.getBoundingClientRect();
    const x = ((event.clientX - left) / width) * 100;
    const y = ((event.clientY - top) / height) * 100;
    this.zoomOrigin.set(`${x}% ${y}%`);
  }

  handleMouseLeave() {
    this.zoomOrigin.set('center');
  }

  showPopup() {
    this.displayImagePopup.set(true);
  }

  copyLink() {
    const url = this.document.location.href;
    navigator.clipboard.writeText(url).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Gekopieerd',
        detail: 'Link naar product is gekopieerd naar klembord'
      });
    });
  }

  shareOnFacebook() {
    const url = encodeURIComponent(this.document.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  }

  shareOnInstagram() {
    // Instagram heeft geen directe sharer URL voor web links naar posts.
    // Meestal verwijzen we naar het profiel of geven we aan dat ze de link kunnen kopiëren.
    // Voor nu openen we Instagram en geven we een melding.
    this.messageService.add({
      severity: 'info',
      summary: 'Instagram',
      detail: 'Kopieer de link om deze te delen op Instagram'
    });
    this.copyLink();
    window.open('https://www.instagram.com/', '_blank');
  }

  ngOnInit() {
    this.route.params.pipe(
      map(params => params['id']),
      switchMap(id => this.store.select(selectProductById(id))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(product => {
      this.product.set(product);
      this.selectedImageIndex.set(0);
      if (product) {
        const title = this.title() || product.title;
        this.titleService.setTitle(product.seoTitle || title);
        this.metaService.updateTag({ name: 'description', content: product.seoDescription || '' });

        // OpenGraph
        this.metaService.updateTag({ property: 'og:title', content: product.ogTitle || product.seoTitle || title });
        this.metaService.updateTag({ property: 'og:description', content: product.ogDescription || product.seoDescription || '' });
        if (product.images && product.images.length > 0) {
          this.metaService.updateTag({ property: 'og:image', content: product.images[0] });
        }
      }
    });
  }
}
